import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";

export interface Column<T> {
  id: string;
  /** Header cell, already translated; mono uppercase by the table. */
  header?: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "left" | "right";
  /** Extra classes on every body cell of this column, e.g. "font-mono text-data". */
  className?: string;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string;
  /** CSS grid-template-columns; one track per column. */
  template: string;
  /** Padding density: cards use `regular`, the rail uses `compact`. */
  density?: "regular" | "compact";
  selectedKey?: string;
  onSelect?: (row: T) => void;
  /** Background tone per row: the Progress screen tints a failed row warn. */
  rowTone?: (row: T) => "none" | "accent" | "warn" | "danger" | undefined;
  /** Shown instead of the body when there are no rows. */
  empty?: ReactNode;
  /** Rendered after the rows on the same template, e.g. a TotalsRow. */
  footer?: ReactNode;
  className?: string;
}

const TONE = {
  none: "",
  accent: "bg-accent-soft",
  warn: "bg-warn-soft",
  danger: "bg-danger-soft",
} as const;

type RowTone = keyof typeof TONE;

const ROW_BASE = "grid w-full items-center border-b border-line-soft text-left text-ui";
const SELECTED_EDGE = "inset 3px 0 0 var(--color-accent)";

function rowClass(pad: string, gap: string, tone: RowTone, selected: boolean, clickable: boolean) {
  return cx(
    ROW_BASE,
    pad,
    gap,
    TONE[tone],
    selected && "bg-accent-soft",
    clickable && "hover:bg-sunken",
    clickable && selected && "hover:bg-accent-soft",
  );
}

function rowStyle(template: string, selected: boolean) {
  return { gridTemplateColumns: template, boxShadow: selected ? SELECTED_EDGE : undefined };
}

/**
 * Grid table with hard borders: mono uppercase header row, one grid row per
 * item. Selection is a row click and a left accent edge, like the Review rail.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  template,
  density = "regular",
  selectedKey,
  onSelect,
  rowTone,
  empty,
  footer,
  className = "",
}: DataTableProps<T>) {
  const { t } = useTranslation("components");
  const pad = density === "compact" ? "px-3.5 py-2" : "px-6 py-2.5";
  const gap = density === "compact" ? "gap-0" : "gap-3";
  const hasHeader = columns.some((c) => c.header !== undefined);
  const toneOfRow = (row: T): RowTone => rowTone?.(row) ?? "none";
  return (
    <div data-component="DataTable" className={cx("flex flex-col", className)}>
      {hasHeader ? (
        <div
          data-row="header"
          className={cx("grid items-center border-y border-line label-mono", pad, gap)}
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((c) => (
            <span key={c.id} className={cx("truncate", c.align === "right" && "text-right")}>
              {c.header}
            </span>
          ))}
        </div>
      ) : null}
      {rows.length === 0 ? (
        <div className="px-6 py-6 text-center text-ui text-ink-3">{empty ?? t("table.empty")}</div>
      ) : null}
      {rows.map((row) => {
        const key = rowKey(row);
        const selected = selectedKey !== undefined && key === selectedKey;
        const tone = toneOfRow(row);
        const className = rowClass(pad, gap, tone, selected, onSelect !== undefined);
        const style = rowStyle(template, selected);
        const cells = columns.map((c) => (
          <span
            key={c.id}
            className={cx("min-w-0 truncate", c.align === "right" && "text-right", c.className)}
          >
            {c.cell(row)}
          </span>
        ));
        return onSelect ? (
          <button
            key={key}
            type="button"
            data-row="body"
            data-key={key}
            aria-pressed={selected}
            onClick={() => onSelect(row)}
            className={className}
            style={style}
          >
            {cells}
          </button>
        ) : (
          <div key={key} data-row="body" data-key={key} className={className} style={style}>
            {cells}
          </div>
        );
      })}
      {footer}
    </div>
  );
}
